'use client';

/**
 * API namespace `stores` (sub-namespace within `seller`) — Founder
 * directive 2026-10-28 (SUBSTORES PR 2 — FE switcher + management page +
 * REMOVE 10-store limit).
 *
 * Multi-store per user — 4 typed wrappers for the sub-stores management
 * endpoints:
 *   - `list()`         → GET    /api/seller/stores
 *   - `create(body)`   → POST   /api/seller/stores
 *   - `setPrimary(id)` → PATCH  /api/seller/stores/:sellerId/primary
 *   - `softDelete(id)` → DELETE /api/seller/stores/:sellerId
 *
 * Authz (BE router-level — applied by `seller-routes.ts` before the
 * sub-router): `requireAuth` + `requireSellerKycApproved` (the user must
 * already have at least ONE active KYC-approved store before they can
 * create ADDITIONAL sub-stores). The FIRST store is created via the
 * existing `POST /api/seller/register` endpoint (separate flow with
 * `sellerPurchaseLimiter` + `requireGeoFields` middleware).
 *
 * Founder directive 2026-10-28 (SUBSTORES PR 2): the original 10-store
 * cap was REMOVED — users can now manage UNLIMITED sub-stores per user.
 * The BE no longer throws `SELLER_STORE_LIMIT_REACHED`.
 *
 * §6 Anti-Invención: VERIFICADA — request/response shapes mirror the BE
 * `store-controller.ts` handlers + the shared `SellerStore*` types in
 * `@profileia/shared/types/seller`. NO invented values, NO invented
 * thresholds.
 *
 * Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2); SPEC-081
 *           Fase 2 §5.1 (seller routes — pattern reuse); §10.4 (Portal
 *           de Vendedores).
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` calls.
 * Anti-P-043 (AbortController): every async method accepts an
 *              AbortSignal + passes it to `apiFetch`.
 * Anti-P-382: REUSE the same `apiFetch` core as other namespaces — NO
 *              parallel fetch mechanism.
 * Anti-P-383: file ≤500 L (objetivo).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  SellerRegistrationRequest,
  SellerStoreSummary,
  SellerStoreCreateResponse,
  SellerStoreSetPrimaryResponse,
  SellerStoreSoftDeleteResponse,
} from '../core/shared-types';

export const stores = {
  /**
   * GET /api/seller/stores — list ALL stores owned by the authenticated
   * user (excluding soft-deleted).
   *
   * Response: `{ data: SellerStoreSummary[] }` sorted by `createdAt` asc
   * (oldest first — the primary store appears first per the isPrimary
   * auto-set rule on the FIRST store a user creates).
   *
   * The response includes ALL Seller fields except sensitive admin-only
   * fields (kycDocuments, taxId, businessName) — the seller can see
   * their own stores' KYC status + business details.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` + KYC gate.
   *
   * Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2) — list.
   */
  async list(
    signal?: AbortSignal,
  ): Promise<SellerStoreSummary[]> {
    // FIX (smoke test staging 2026-10-30): apiFetch auto-unwraps `.data`
    // from the BE response (`{ data: SellerStoreSummary[] }` → returns the
    // inner array directly). The pre-fix code declared `apiFetch<{ data:
    // SellerStoreSummary[] }>` (treating the generic as the FULL response
    // shape) + then did `res.data` — but `res` was ALREADY the unwrapped
    // array, so `res.data` was `undefined` → `setStores(undefined)` →
    // `stores.length` threw `Cannot read properties of undefined`. This
    // caused the entire `/seller/tiendas` page to crash with an Error
    // Boundary ("Algo salió mal").
    //
    // Fix: declare the generic as the UNWRAPPED shape (just the array)
    // + return `res` directly (apiFetch already unwrapped `.data`).
    // Anti-P-382: root fix — align with the apiFetch contract (it
    // auto-unwraps `.data` when the body has a `data` field). Anti-P-041:
    // no `as any` — the type now matches the actual return value.
    return apiFetch<SellerStoreSummary[]>(
      '/seller/stores',
      { signal },
    );
  },
  /**
   * POST /api/seller/stores — create a NEW sub-store.
   *
   * Body: the SAME shape as `POST /api/seller/register` (storeName,
   * businessType, businessName?, taxId?, kycDocuments?) — validated by
   * the canonical `sellerRegistrationSchema` (Zod — same schema as the
   * first-store registration).
   *
   * Delegates to `sellerService.registerSeller` on the BE (Anti-P-382
   * — REUSE the existing first-store creation function). The new store
   * is auto-marked `isPrimary=false` (the user already has a primary
   * store — the `requireSellerKycApproved` gate guarantees it).
   *
   * Response: 201 Created with `{ data: { sellerId, storeSlug } }`.
   *
   * Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2) — create.
   */
  async create(
    body: SellerRegistrationRequest,
    signal?: AbortSignal,
  ): Promise<SellerStoreCreateResponse> {
    // FIX (smoke test staging 2026-10-30): same double-unwrap bug as
    // `list()` above — apiFetch auto-unwraps `.data`, so we declare the
    // generic as the unwrapped shape + return `res` directly.
    return apiFetch<SellerStoreCreateResponse>(
      '/seller/stores',
      { method: 'POST', body: JSON.stringify(body), signal },
    );
  },
  /**
   * PATCH /api/seller/stores/:sellerId/primary — set target as primary.
   *
   * Atomically (single round-trip): unsets `isPrimary` on the user's
   * other non-deleted stores + sets `isPrimary: true` on the target
   * store. Defense-in-depth: the BE has a partial unique index
   * `{ userId, isPrimary }` enforcing "at most one primary per user"
   * at the DB level.
   *
   * Response: `{ data: { sellerId, isPrimary: true } }`.
   *
   * Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2) — setPrimary.
   */
  async setPrimary(
    sellerId: string,
    signal?: AbortSignal,
  ): Promise<SellerStoreSetPrimaryResponse> {
    // FIX (smoke test staging 2026-10-30): same double-unwrap bug as
    // `list()` above — apiFetch auto-unwraps `.data`, so we declare the
    // generic as the unwrapped shape + return `res` directly.
    return apiFetch<SellerStoreSetPrimaryResponse>(
      `/seller/stores/${encodeURIComponent(sellerId)}/primary`,
      { method: 'PATCH', signal },
    );
  },
  /**
   * DELETE /api/seller/stores/:sellerId — soft-delete a sub-store.
   *
   * Sets `status='deleted'` + `isPrimary=false` (releases the partial
   * unique index slot). The Seller doc is preserved for audit +
   * dispute queryability — orders remain queryable.
   *
   * Rules (BE-enforced):
   *   - 404 STORE_NOT_FOUND if the store doesn't exist / isn't owned.
   *   - 409 STORE_DELETED if the store was already soft-deleted.
   *   - 409 CANNOT_DELETE_PRIMARY if the user has OTHER non-deleted
   *     stores (the user must set another as primary FIRST).
   *
   * Response: `{ data: { sellerId, status: 'deleted' } }`.
   *
   * Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2) — softDelete.
   */
  async softDelete(
    sellerId: string,
    signal?: AbortSignal,
  ): Promise<SellerStoreSoftDeleteResponse> {
    // FIX (smoke test staging 2026-10-30): same double-unwrap bug as
    // `list()` above — apiFetch auto-unwraps `.data`, so we declare the
    // generic as the unwrapped shape + return `res` directly.
    return apiFetch<SellerStoreSoftDeleteResponse>(
      `/seller/stores/${encodeURIComponent(sellerId)}`,
      { method: 'DELETE', signal },
    );
  },
};
